import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Fade,
  Card,
  CardContent,
  Chip,
  Grid,
  Divider,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ThankYouImage from "../../../assets/images/thankyou.png";

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    background: "linear-gradient(135deg, #ffffff 30%, #f0f4ff 100%)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
    padding: theme.spacing(3),
    maxWidth: "500px",
    width: "90%",
    transition: "all 0.3s ease-in-out",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(2),
    },
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  "& .success-icon": {
    fontSize: "4rem",
    color: theme.palette.success.main,
    animation: "pulse 2s infinite",
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      opacity: 1,
    },
    "50%": {
      transform: "scale(1.2)",
      opacity: 0.7,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
}));

const ThankYouImg = styled("img")(({ theme }) => ({
  width: "100px",
  height: "auto",
  margin: "0 auto",
  display: "block",
  [theme.breakpoints.down("sm")]: {
    width: "80px",
  },
}));

const OrderDetailsBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: "8px",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const CloseButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
  color: "#fff",
  padding: theme.spacing(1, 3),
  borderRadius: "8px",
  "&:hover": {
    background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
  },
}));

const ThankYouModal = ({
  show,
  onHide,
  totalPrice,
  state,
  orderId,
  orderDetails,
}) => {
  const { donationFrom } = useSelector((state) => state.gurdwaraDonation);
  const navigate = useNavigate();

  const onClose = () => {
    onHide();
    if (state?.hire) {
      navigate("/dashboardpage/orders");
    } else if (state?.item) {
      navigate(`/dashboardpage/my-items?tab=Purchased`);
    } else if (state?.donation && donationFrom !== "GURDWARA_NEED") {
      navigate("/dashboardpage/gurdwaramanagement");
    } else {
      navigate("/dashboardpage/inbox");
    }
    window.scroll(0, 0);
  };

  return (
    <StyledDialog
      open={show}
      onClose={onHide}
      TransitionComponent={Fade}
      transitionDuration={900}
      aria-labelledby="thank-you-dialog-title"
      aria-describedby="thank-you-dialog-description"
      maxWidth="md"
    >
      <DialogContent>
        <IconContainer>
          <CheckCircleIcon className="success-icon" />
        </IconContainer>

        {!state?.chatPay && !state?.donation && (
          <ThankYouImg src={ThankYouImage} alt="Thank You" />
        )}
        <Typography
          variant="h5"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold", color: "#1976d2", mt: 2 }}
        >
          {totalPrice !== 20 &&
          (state?.chatPay ||
            state?.invoicePay ||
            (state?.donation && donationFrom !== "GurdwaraNeed"))
            ? "PAYMENT COMPLETED SUCCESSFULLY!"
            : "Thank You for Your Order!"}
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: 2 }}>
          {totalPrice === 20 ? (
            "The customer phone number has been made available within the inbox section."
          ) : (
            <>
              {state?.chatPay ||
              state?.invoicePay ||
              (state?.donation && donationFrom !== "GurdwaraNeed")
                ? "Your payment has been processed successfully."
                : "Your booking is finalized once the vendor approves your transaction. Vendor has the right to decline the order request. You will only be charged upon order approval."}
            </>
          )}
        </Typography>

        {(orderId || orderDetails) && (
          <OrderDetailsBox>
            {orderId && (
              <Typography variant="h6" className="text-primary fw-bold mb-3">
                Order ID: #{orderId}
              </Typography>
            )}

            {/* Main Order Card */}
            {orderDetails && (
              <Card
                variant="outlined"
                sx={{ borderRadius: 3, overflow: "hidden", boxShadow: 3 }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <Typography variant="h5" className="fw-bold">
                      Order Summary
                    </Typography>
                    <Chip
                      label={orderDetails.orderStatus || "PENDING"}
                      color={
                        orderDetails.orderStatus === "APPROVED"
                          ? "success"
                          : orderDetails.orderStatus === "REJECTED"
                          ? "error"
                          : "warning"
                      }
                      size="small"
                      sx={{ fontWeight: "bold", px: 1 }}
                    />
                  </Box>

                  <Grid container spacing={3}>
                    {/* Left Column */}
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Service Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Box mb={2}>
                        <Typography variant="body2">
                          <strong>Date:</strong>{" "}
                          {orderDetails.reserveDate || "Not set"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Time:</strong>{" "}
                          {orderDetails.startTime || "Not set"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Message:</strong>{" "}
                          {orderDetails.Message ||
                            orderDetails.aboutAppointment ||
                            "No message"}
                        </Typography>
                      </Box>

                      {/* Package Details - Beautiful Card */}
                      {orderDetails.packageDetails &&
                        (() => {
                          let pkg;
                          try {
                            pkg = JSON.parse(orderDetails.packageDetails);
                          } catch {
                            return null;
                          }

                          return pkg ? (
                            <Box
                              mt={3}
                              p={3}
                              bgcolor="#f8fff9"
                              borderRadius={2}
                              border="1px dashed #4caf50"
                            >
                              <Typography
                                variant="subtitle2"
                                className="fw-bold text-success mb-2"
                              >
                                Package Selected: {pkg.title}
                              </Typography>
                              {pkg.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 2 }}
                                >
                                  {pkg.description}
                                </Typography>
                              )}

                              {pkg.packages
                                ?.filter((p) => p.checked)
                                .map((item, i) => (
                                  <Box
                                    key={i}
                                    display="flex"
                                    justifyContent="space-between"
                                    py={0.5}
                                  >
                                    <span>{item.title}</span>
                                    <strong>
                                      ${item.price} × {item.units}
                                    </strong>
                                  </Box>
                                ))}

                              <Divider sx={{ my: 1.5 }} />
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                fontWeight="bold"
                              >
                                <span>Total Package Price</span>
                                <span className="text-success fs-5">
                                  ${pkg.totalPrice || 0}
                                </span>
                              </Box>

                              {pkg.guestCount && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  textAlign="right"
                                  color="text.secondary"
                                >
                                  For {pkg.guestCount} guests
                                </Typography>
                              )}
                            </Box>
                          ) : null;
                        })()}
                    </Grid>

                    {/* Right Column */}
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Payment & Status
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Box
                        display="flex"
                        justifyContent="space-between"
                        mb={1.5}
                      >
                        <span>
                          <strong>Total Amount</strong>
                        </span>
                        <span className="fw-bold text-primary fs-5">
                          $
                          {parseFloat(orderDetails.totalAmount || 0).toFixed(2)}
                        </span>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <span>Discount</span>
                        <span>
                          ${parseFloat(orderDetails.Discount || 0).toFixed(2)}
                        </span>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mb={2}>
                        <span>Paid Amount</span>
                        <span
                          className={
                            orderDetails.paymentStatus === "COMPLETED"
                              ? "text-success"
                              : "text-warning"
                          }
                        >
                          <strong>
                            $
                            {parseFloat(orderDetails.paidAmount || 0).toFixed(
                              2
                            )}
                          </strong>
                        </span>
                      </Box>

                      <Alert
                        severity={
                          orderDetails.paymentStatus === "COMPLETED"
                            ? "success"
                            : orderDetails.paymentStatus === "PENDING"
                            ? "warning"
                            : "info"
                        }
                        sx={{ borderRadius: 2 }}
                      >
                        Payment Status:{" "}
                        <strong>
                          {orderDetails.paymentStatus || "PENDING"}
                        </strong>
                      </Alert>
                    </Grid>
                  </Grid>

                  {/* Footer */}
                  <Box mt={4} textAlign="center">
                    <Typography variant="caption" color="text.secondary">
                      Order placed on{" "}
                      {new Date(orderDetails.createdAt).toLocaleDateString()} at{" "}
                      {new Date(orderDetails.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </OrderDetailsBox>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        <CloseButton onClick={onClose}>Close</CloseButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default ThankYouModal;
